import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const MarketPage = () => {
  const [products, setProducts] = useState([]);
  const [userCoins, setUserCoins] = useState(0);
  const [isUser, setIsUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    checkUser();
    fetchProducts();
    
    // Periyodik olarak ürünleri yenile (her 30 saniyede bir) - sadece sayfa görünürken
    let interval;
    const startInterval = () => {
      if (interval) clearInterval(interval);
      interval = setInterval(() => {
        if (!document.hidden) {
          fetchProducts(false); // loading state'i gösterme
        }
      }, 30000); // 30 saniye
    };
    
    startInterval();
    
    // Sayfa görünür olduğunda yenile ve interval'i başlat
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchProducts(false);
        startInterval();
      } else {
        if (interval) clearInterval(interval);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Window focus olduğunda yenile
    const handleFocus = () => {
      fetchProducts(false);
    };
    window.addEventListener('focus', handleFocus);
    
    return () => {
      if (interval) clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    if (isUser) {
      fetchUserCoins();
    }
  }, [isUser]);

  const checkUser = () => {
    const token = localStorage.getItem('userToken');
    setIsUser(!!token);
  };

  const fetchUserCoins = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.get('/api/users/me/coins', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserCoins(response.data.coins || 0);
    } catch (error) {
      console.error('Error fetching user coins:', error);
    }
  };

  const fetchProducts = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await axios.get('/api/market');
      setProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]); // Hata durumunda boş liste göster
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handlePurchase = async (product, quantity = 1) => {
    if (!isUser) {
      alert('Satın alma yapmak için giriş yapmalısınız');
      return;
    }

    if (userCoins < product.price) {
      alert('Yetersiz puan!');
      return;
    }

    if (product.stock !== null && product.stock !== undefined && product.stock < quantity) {
      alert('Yetersiz stok!');
      return;
    }

    if (!window.confirm(`${product.name} ürününü ${product.price} Sagu Coin karşılığında satın almak istediğinize emin misiniz?`)) {
      return;
    }

    try {
      setPurchasing(product.id);
      const token = localStorage.getItem('userToken');
      const response = await axios.post(
        `/api/market/${product.id}/purchase`,
        { quantity },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      alert('Siparişiniz alındı. Ödülünüz yönetici tarafından gönderildiğinde durum güncellenecektir.');
      setUserCoins(response.data.remainingCoins);
      fetchProducts();
      
      // Update userData
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      userData.coins = response.data.remainingCoins;
      localStorage.setItem('userData', JSON.stringify(userData));
      window.dispatchEvent(new Event('userTokenChanged'));
    } catch (error) {
      console.error('Error purchasing product:', error);
      alert(error.response?.data?.error || 'Satın alma sırasında bir hata oluştu');
    } finally {
      setPurchasing(null);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="w-full px-4 py-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gold mb-4 drop-shadow-[0_0_20px_rgba(212,175,55,0.8)]">
            Market
          </h1>
          <p className="text-xl text-white/80 mb-6">
            Sagu Coin'lerinizle harika ödüller kazanın!
          </p>
          
          {isUser ? (
            <div className="inline-flex items-center gap-3 bg-gold/20 px-6 py-3 rounded-xl border border-gold/30">
              <span className="text-gold font-bold text-2xl">
                💰 {userCoins} Sagu Coin
              </span>
            </div>
          ) : (
            <div className="flex gap-4 justify-center">
              <Link
                to="/login"
                className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-8 py-3 rounded-xl transition-all duration-300 shadow-gold"
              >
                Giriş Yap
              </Link>
              <Link
                to="/register"
                className="bg-transparent border-2 border-gold text-gold hover:bg-gold/10 font-bold px-8 py-3 rounded-xl transition-all duration-300"
              >
                Üye Ol
              </Link>
            </div>
          )}
        </div>

        {/* Products Grid */}
        {products.length === 0 ? (
          <div className="text-center text-white/60 py-20">
            <p className="text-xl">Henüz ürün bulunmamaktadır</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => {
              const canAfford = isUser && userCoins >= product.price;
              const inStock = product.stock === null || product.stock === undefined || product.stock > 0;
              const isPurchasing = purchasing === product.id;

              return (
                <div
                  key={product.id}
                  className="bg-gradient-card rounded-xl p-6 border-2 border-gold/30 hover:border-gold transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transform hover:-translate-y-2 flex flex-col relative"
                >
                  {/* Product Type Badge - Top Right Corner */}
                  <div className="absolute top-3 right-3 z-10">
                    <span className={`px-4 py-2 rounded-lg text-sm font-bold shadow-xl backdrop-blur-sm ${
                      product.type === 'cash'
                        ? 'bg-gradient-to-r from-green-500/90 to-emerald-500/90 text-white border-2 border-green-300 drop-shadow-[0_0_8px_rgba(34,197,94,0.5)]'
                        : 'bg-gradient-to-r from-navy-600/90 to-navy-500/90 text-white border-2 border-navy-400 drop-shadow-[0_0_8px_rgba(26,46,57,0.5)]'
                    }`}>
                      {product.type === 'cash' ? '💵 Nakit' : '📦 Ürün'}
                    </span>
                  </div>

                  {/* Product Image */}
                  <div className="mb-4 h-48 bg-navy-900 rounded-lg flex items-center justify-center overflow-hidden relative">
                    {product.imageUrl || product.image_url ? (
                      <img
                        src={`${product.imageUrl || product.image_url}?v=${product.id}`}
                        alt={product.name}
                        className="w-full h-full object-contain"
                        key={`${product.id}-${product.imageUrl || product.image_url}`}
                        onError={(e) => {
                          // Hata durumunda görseli gizle, tekrar deneme
                          console.warn('Image failed to load:', product.imageUrl || product.image_url);
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="text-white/40 text-4xl">
                        {product.type === 'cash' ? '💰' : '📦'}
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <h3 className="text-white font-bold text-lg mb-2">{product.name}</h3>
                  
                  {product.description && (
                    <p className="text-white/70 text-sm mb-4 flex-1">{product.description}</p>
                  )}

                  {/* Price and Stock */}
                  <div className="mb-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gold font-bold text-xl">
                        💰 {product.price} Sagu Coin
                      </span>
                    </div>
                    {product.stock !== null && product.stock !== undefined && (
                      <div className="text-white/60 text-sm">
                        📦 Stok: {product.stock > 0 ? product.stock : 'Tükendi'}
                      </div>
                    )}
                  </div>

                  {/* Purchase Button */}
                  {isUser ? (
                    <button
                      onClick={() => handlePurchase(product)}
                      disabled={!canAfford || !inStock || isPurchasing}
                      className={`w-full py-3 rounded-lg font-bold transition-all duration-300 ${
                        canAfford && inStock
                          ? 'bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 shadow-gold'
                          : 'bg-gray-600 text-gray-300 cursor-not-allowed'
                      }`}
                    >
                      {isPurchasing ? 'Satın Alınıyor...' : 
                       !canAfford ? 'Yetersiz Puan' :
                       !inStock ? 'Stokta Yok' :
                       'Satın Al'}
                    </button>
                  ) : (
                    <Link
                      to="/login"
                      className="block w-full py-3 text-center bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold rounded-lg transition-all duration-300 shadow-gold"
                    >
                      Giriş Yap ve Satın Al
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketPage;
